import "@/index.css";
import { I18nProvider } from "@mux/ui/context";
import { DialogProvider } from "@mux/ui/context/dialog";
import { Font } from "@mux/ui/font";
import { ThemeProvider } from "@mux/ui/theme";
import { themePersistence } from "@/context/theme/persistence";
import { MetaProvider, Title } from "@solidjs/meta";
import {
  type BaseRouterProps,
  Navigate,
  Route,
  type RouteSectionProps,
  Router,
  useLocation,
} from "@solidjs/router";
import {
  type Component,
  createComponent,
  createEffect,
  createMemo,
  createSignal,
  ErrorBoundary,
  type JSX,
  lazy,
  onCleanup,
  type ParentProps,
  Show,
  Suspense,
} from "solid-js";
import { CommandProvider } from "@/context/command";
import { DashboardLoadingScreen } from "@/components/dashboard-loading-screen";
import { MachinesStartupGate } from "@/app-lifecycle/machines-startup-gate";
import { AuthenticationBoundary } from "@/components/authentication-boundary";
import MachineContentProviders from "@/components/machine-content-providers";
import { StarterMessageController } from "@/fleet/starter-message-controller";
import { WorkerPoolProvider } from "@/ui/pierre/worker-context";
import { Worktree } from "@/utils/worktree";

import { LanguageProvider, useLanguage } from "@/context/language";
import { LayoutProvider } from "@/context/layout";
import { ModelsProvider } from "@/context/models";
import { ProviderDataProvider } from "@/hooks/use-providers";
import { CapabilityLibraryProvider } from "@/context/capability-library";
import { AccountMuxOracleProvider } from "@/context/account-mux-oracle";
import { NotificationContactsProvider } from "@/context/notification-contacts";
import { ControlPageSkeleton } from "@/components/control-page";
import { SmallModelRoutesProvider } from "@/context/small-model-routes";
import { GlobalLocalProvider } from "@/context/local";
import { MobilePushProvider } from "@/context/mobile-push";
import { PromptProvider, usePrompt } from "@/context/prompt";
import {
  ServerConnection,
  ServerProvider,
  useServer,
} from "@/context/server";
import { SettingsProvider } from "@/context/settings";
import { ErrorPage } from "./pages/error";

import {
  FleetProvider,
  useFleet,
} from "@/context/fleet";
import { AuthProvider, useAuth } from "@/context/auth";
import { SessionVerification } from "@/components/session-verification";
import { useAppInfo } from "@/context/app-info";
import { useNavigate } from "@/utils/navigation";
import { useTabParams } from "@/utils/link-param";
import {
  machineResumePath,
  parseMachineFromPath,
} from "@/utils/machine-route";
import { loadControlRoute } from "@/utils/control-route-preload";
import { createTouchContextMenu } from "@/utils/touch-hold";
import { AutoMachineConnector } from "@/fleet/auto-machine-controller";
import { dashboardHome } from "@/fleet/machine-choice";
import {
  ChunkRecoveryScreen,
  ChunkReloadController,
  useChunkReload,
} from "@/app-lifecycle/chunk-reload-controller";
import { AppUpdateController, AppUpdateNavigation, useAppUpdateHold } from "@/app-lifecycle/app-update-controller";
import { RoutePrefetchController } from "@/app-lifecycle/route-prefetch-controller";
import { MachineRouteController } from "@/app-lifecycle/machine-route-controller";
import {
  createMachineHomeResume,
  MachineResumeController,
  useMachineResume,
} from "@/app-lifecycle/machine-resume-controller";

type EmptyComponentProps = Record<string, never>;

function lazyWithChunkReload<Props extends object = EmptyComponentProps>(
  loader: () => Promise<{ default: Component<Props> }>,
): Component<Props> {
  return (props) => {
    const scheduleReload = useChunkReload();
    const LazyComponent = lazy(() =>
      loader().catch((error) => {
        if (scheduleReload(error)) {
          return { default: ChunkRecoveryScreen };
        }
        throw error;
      }),
    );
    return createComponent(LazyComponent, props);
  };
}

const SharedChatPage = lazyWithChunkReload(() => import("@/pages/shared-chat/page"));
const MuxProvidersPage = lazyWithChunkReload(() => import("@/pages/mux-providers"));
const MuxMachinesPage = lazyWithChunkReload(() => import("@/pages/mux-machines"));
const MCPOAuthCallback = lazyWithChunkReload(() => import("@/pages/mcp-oauth-callback"));
const AuthScreen = lazyWithChunkReload(() =>
  import("@/pages/auth").then((module) => ({ default: module.AuthScreen as Component<EmptyComponentProps> })) as Promise<{ default: Component<EmptyComponentProps> }>,
);
const LandingPage = lazyWithChunkReload(() =>
  import("@/pages/landing").then((module) => ({ default: module.LandingPage as Component<EmptyComponentProps> })) as Promise<{ default: Component<EmptyComponentProps> }>,
);
const SessionProviders = lazyWithChunkReload<ParentProps>(() =>
  import("@/components/session-providers").then((module) => ({ default: module.SessionProviders as Component<ParentProps> })) as Promise<{ default: Component<ParentProps> }>,
);
const WorkspaceLayout = lazyWithChunkReload<ParentProps<{ disableHealthCheck?: boolean }>>(() =>
  import("@/pages/layout").then((module) => ({ default: module.default as Component<ParentProps<{ disableHealthCheck?: boolean }>> })) as Promise<{ default: Component<ParentProps<{ disableHealthCheck?: boolean }>> }>,
);
const DirectoryLayout = lazyWithChunkReload<RouteSectionProps>(() =>
  import("@/pages/directory-layout").then((module) => ({ default: module.default as Component<RouteSectionProps> })) as Promise<{ default: Component<RouteSectionProps> }>,
);
const TrashPreviewRoute = lazyWithChunkReload<RouteSectionProps>(() =>
  import("@/pages/trash-preview").then((module) => ({ default: module.default as Component<RouteSectionProps> })) as Promise<{ default: Component<RouteSectionProps> }>,
);
const Home = lazyWithChunkReload(() =>
  import("@/pages/home").then((module) => ({ default: module.default as Component<EmptyComponentProps> })) as Promise<{ default: Component<EmptyComponentProps> }>,
);
const FleetHome = lazyWithChunkReload<EmptyComponentProps>(() =>
  import("@/pages/home").then((module) => ({ default: module.FleetHome })),
);
const ProjectSummary = lazyWithChunkReload(
  () => import("@/pages/project-hub/project-summary").then((module) => ({ default: module.default as Component<EmptyComponentProps> })) as Promise<{ default: Component<EmptyComponentProps> }>,
);
const ProvidersPage = lazyWithChunkReload<EmptyComponentProps>(
  () => loadControlRoute("providers") as Promise<{ default: Component<EmptyComponentProps> }>,
);
const UsagePage = lazyWithChunkReload<EmptyComponentProps>(
  () => loadControlRoute("usage") as Promise<{ default: Component<EmptyComponentProps> }>,
);
const AutomationsPage = lazyWithChunkReload<EmptyComponentProps>(
  () => loadControlRoute("automations") as Promise<{ default: Component<EmptyComponentProps> }>,
);
const ToolsPage = lazyWithChunkReload<EmptyComponentProps>(
  () => loadControlRoute("tools") as Promise<{ default: Component<EmptyComponentProps> }>,
);
const SettingsPage = lazyWithChunkReload<EmptyComponentProps>(
  () => loadControlRoute("settings") as Promise<{ default: Component<EmptyComponentProps> }>,
);
const SecretsPage = lazyWithChunkReload(() => import("@/pages/secrets"));
const Machines = lazyWithChunkReload(() =>
  import("@/pages/machines").then((module) => ({ default: module.default as Component<EmptyComponentProps> })) as Promise<{ default: Component<EmptyComponentProps> }>,
);

const Loading = (props: { label?: string }) => (
  <DashboardLoadingScreen label={props.label ?? "Loading Kettle"} />
);

const ProvidersPageRoute = () => (
  <Suspense fallback={<ControlPageSkeleton title="Providers" />}>
    <ProvidersPage />
  </Suspense>
);

const UsagePageRoute = () => (
  <Suspense fallback={<ControlPageSkeleton title="Usage" width="wide" />}>
    <UsagePage />
  </Suspense>
);

const AutomationsPageRoute = () => (
  <Suspense fallback={<ControlPageSkeleton title="Automations" />}>
    <AutomationsPage />
  </Suspense>
);

const ToolsPageRoute = () => (
  <Suspense fallback={<ControlPageSkeleton title="Capabilities" />}>
    <ToolsPage />
  </Suspense>
);

const SettingsPageRoute = () => (
  <CommandProvider>
    <Suspense fallback={<ControlPageSkeleton title="Settings" />}>
      <SettingsPage />
    </Suspense>
  </CommandProvider>
);

const SecretsPageRoute = () => (
  <Suspense fallback={<ControlPageSkeleton title="Secrets" />}>
    <SecretsPage />
  </Suspense>
);

const DashboardHomeRoute = () => (
  <Suspense fallback={<Loading />}>
    <FleetHome />
  </Suspense>
);

const OracleRoute = () => null;

const OracleSummaryRoute = () => (
  <SessionProviders>
    <Suspense fallback={<Loading />}>
      <ProjectSummary />
    </Suspense>
  </SessionProviders>
);

/** Bare /dashboard opens a ready machine, or the machines page when none is reachable. */
function DashboardHomeRedirect() {
  const server = useServer();
  const fleet = useFleet();
  const location = useLocation();
  const destination = createMemo(() => {
    const home = dashboardHome(
      fleet.views(),
      [server.name, server.lastMachine],
      fleet.machines.loading,
    );
    if (!home) return "";
    if (home === "machines") return "/dashboard/machines";
    return `/dashboard/m/${encodeURIComponent(home.machine)}${location.search}${location.hash}`;
  });

  return (
    <Show
      when={destination()}
      fallback={
        <DashboardLoadingScreen
          label={
            fleet.machines.loading ? "Loading your machines" : "Preparing dashboard"
          }
        />
      }
    >
      {(href) => <Navigate href={href()} />}
    </Show>
  );
}

function MachineResumeRedirect(props: { machine: string; route: string }) {
  const navigate = useNavigate();
  const resume = useMachineResume();
  createEffect(() => {
    resume.markResumed(props.machine);
    navigate(`/dashboard${machineResumePath(props.machine, props.route)}`, {
      replace: true,
    });
  });
  return <DashboardLoadingScreen label="Restoring your last chat" />;
}

const HomeRoute = () => {
  const location = useLocation();
  const server = useServer();
  const resume = createMachineHomeResume({
    machine: () => parseMachineFromPath(location.pathname) || "",
    state: useMachineResume(),
    routes: server,
  });

  return (
    <Show
      when={resume()}
      keyed
      fallback={
        <Suspense fallback={<Loading />}>
          <Home />
        </Suspense>
      }
    >
      {(route) => <MachineResumeRedirect machine={parseMachineFromPath(location.pathname) || ""} route={route} />}
    </Show>
  );
};

const MachinesRoute = () => (
  <Suspense fallback={<ControlPageSkeleton title="Machines" />}>
    <Machines />
  </Suspense>
);

const SessionPlaceholder = () => null;
const SessionIndexRoute = () => <Navigate href="session" />;

function UiI18nBridge(props: ParentProps) {
  const language = useLanguage();
  return (
    <I18nProvider value={{ locale: language.intl, t: language.t }}>
      {props.children}
    </I18nProvider>
  );
}

declare global {
  interface Window {
    __MUX__?: {
      updaterEnabled?: boolean;
      deepLinks?: string[];
      wsl?: boolean;
    };
    api?: {
      setTitlebar?: (theme: { mode: "light" | "dark" }) => Promise<void>;
    };
  }
}

/**
 * Dashboard-wide providers, mounted once. Machine-scoped state lives in the
 * machine scopes the layout creates below them, so moving between machines
 * never tears these down.
 */
function FleetShellProviders(props: ParentProps) {
  onCleanup(() => Worktree.dispose());
  return (
    <WorkerPoolProvider>
      <MachineContentProviders>
        <SettingsProvider>
          <LayoutProvider>
            <MobilePushProvider>
              <GlobalLocalProvider>
                <PromptProvider>
                  <CommandProvider>
                    <StarterMessageController />
                    <PromptUpdateHold />
                    {props.children}
                  </CommandProvider>
                </PromptProvider>
              </GlobalLocalProvider>
            </MobilePushProvider>
          </LayoutProvider>
        </SettingsProvider>
      </MachineContentProviders>
    </WorkerPoolProvider>
  );
}

/** An update reload waits for prompt drafts still being saved. */
function PromptUpdateHold() {
  useAppUpdateHold(usePrompt().settled);
  return null;
}

function _splitBasicAuth(auth?: string) {
  if (!auth) return {};
  const index = auth.indexOf(":");
  if (index === -1) return { username: auth };
  return { username: auth.slice(0, index), password: auth.slice(index + 1) };
}

function AppErrorFallback(props: { error: unknown }) {
  const scheduleReload = useChunkReload();
  if (scheduleReload(props.error)) {
    return <ChunkRecoveryScreen />;
  }
  return <ErrorPage error={props.error} />;
}

export function AppBaseProviders(props: ParentProps) {
  createTouchContextMenu();
  // index.html's <title> names the tab while the app loads. The first <title>
  // in the document names the tab and @solidjs/meta appends its own, so the
  // static one would outrank every page title.
  document.head.querySelector("title")?.remove();
  return (
    <ChunkReloadController>
      <AppUpdateController>
      <MetaProvider>
      <Title>Kettle</Title>
      <Font />
      <ThemeProvider
        persistence={themePersistence}
        onThemeApplied={(_, mode) => {
          void window.api?.setTitlebar?.({ mode });
        }}
      >
        <LanguageProvider>
          <UiI18nBridge>
            <ErrorBoundary fallback={(error) => <AppErrorFallback error={error} />}>
              <DialogProvider>
                {props.children}
              </DialogProvider>
            </ErrorBoundary>
          </UiI18nBridge>
        </LanguageProvider>
      </ThemeProvider>
      </MetaProvider>
      </AppUpdateController>
    </ChunkReloadController>
  );
}

function ServerScopedProviders(props: {
  routerProps: { children?: JSX.Element };
  disableHealthCheck?: boolean;
  appChildren?: JSX.Element;
}) {
  return (
    <FleetShellProviders>
      {props.appChildren}
      <WorkspaceLayout disableHealthCheck={props.disableHealthCheck}>{props.routerProps.children}</WorkspaceLayout>
    </FleetShellProviders>
  );
}

type AppInterfaceProps = {
  children?: JSX.Element;
  defaultServer: ServerConnection.Key;
  servers?: Array<ServerConnection.Any>;
  startInactive?: boolean;
  router?: Component<BaseRouterProps>;
  disableHealthCheck?: boolean;
  base?: string;
};

function MachineRouteProviders(
  props: AppInterfaceProps & { routerProps: { children?: JSX.Element } },
) {
  return (
    <Show
      when={props.startInactive}
      fallback={
        <ServerScopedProviders
          routerProps={props.routerProps}
          disableHealthCheck={props.disableHealthCheck}
          appChildren={props.children}
        />
      }
    >
      <AutoMachineConnector>
        <MachineRouteController>
          <ServerScopedProviders
            routerProps={props.routerProps}
            disableHealthCheck={props.disableHealthCheck}
            appChildren={props.children}
          />
        </MachineRouteController>
      </AutoMachineConnector>
    </Show>
  );
}

function DashboardRouteContent(
  props: AppInterfaceProps & { routerProps: { children?: JSX.Element } },
) {
  return (
    <MachinesStartupGate>
      <MachineRouteProviders {...props} />
    </MachinesStartupGate>
  );
}

function DashboardRouteProviders(
  props: AppInterfaceProps & { routerProps: { children?: JSX.Element } },
) {
  return (
    <AuthenticationBoundary>
      <ServerProvider
        defaultServer={props.defaultServer}
        servers={props.servers}
        startInactive={props.startInactive}
      >
        <FleetProvider>
          <ProviderDataProvider>
            <CapabilityLibraryProvider>
              <AccountMuxOracleProvider>
                <NotificationContactsProvider>
                  <ModelsProvider>
                    <SmallModelRoutesProvider>
                      <DashboardRouteContent {...props} />
                    </SmallModelRoutesProvider>
                  </ModelsProvider>
                </NotificationContactsProvider>
              </AccountMuxOracleProvider>
            </CapabilityLibraryProvider>
          </ProviderDataProvider>
        </FleetProvider>
      </ServerProvider>
    </AuthenticationBoundary>
  );
}

function ForegroundDashboard(props: AppInterfaceProps & { routerProps: { children?: JSX.Element } }) {
  const [started, setStarted] = createSignal(typeof document === "undefined" || document.visibilityState === "visible");
  if (!started() && typeof document !== "undefined") {
    const start = () => {
      if (document.visibilityState !== "visible") return;
      setStarted(true);
      document.removeEventListener("visibilitychange", start);
    };
    document.addEventListener("visibilitychange", start);
    onCleanup(() => document.removeEventListener("visibilitychange", start));
  }
  return (
    <Show when={started()} fallback={<DashboardLoadingScreen label="Waiting for this tab" />}>
      <DashboardRouteProviders {...props} />
    </Show>
  );
}

function HostedRouterRoot(
  props: AppInterfaceProps & { routerProps: { children?: JSX.Element } },
) {
  const auth = useAuth();
  const location = useLocation();
  const appInfo = useAppInfo();
  const requiresAuth = appInfo.accountAuth;
  const dashboard = createMemo(
    () =>
      location.pathname === "/dashboard" ||
      location.pathname.startsWith("/dashboard/"),
  );

  useTabParams();

  return (
    <>
      <RoutePrefetchController />
      <AppUpdateNavigation />
      <Show when={location.pathname === "/share"} fallback={<SessionVerification>
        <Show when={dashboard()} fallback={props.routerProps.children}>
          <Show
            when={!requiresAuth || auth.authenticated()}
            fallback={<Navigate href="/login" />}
          >
            <ForegroundDashboard {...props} routerProps={props.routerProps} />
          </Show>
        </Show>
      </SessionVerification>}>
        {props.routerProps.children}
      </Show>
    </>
  );
}

function HostedApp(props: AppInterfaceProps) {
  const auth = useAuth();
  const LoginRoute = () => (
    <Show
      when={auth.authenticated()}
      fallback={
        <Suspense fallback={<Loading />}>
          <AuthScreen />
        </Suspense>
      }
    >
      <Navigate href="/dashboard/home" />
    </Show>
  );
  return (
    <MachineResumeController>
      <Router
        root={(routerProps) => (
          <HostedRouterRoot {...props} routerProps={routerProps} />
        )}
      >
      <Route
        path="/"
        component={() => (
          <Suspense fallback={<Loading />}>
            <LandingPage />
          </Suspense>
        )}
      />
      <Route path="/share" component={() => <Suspense fallback={<Loading />}><SharedChatPage /></Suspense>} />
      <Route path="/login" component={LoginRoute} />
      <Route path="/oauth/mcp/:tool/:machine" component={() => <Suspense fallback={<Loading />}><MCPOAuthCallback /></Suspense>} />
      <Route path="/providers" component={() => <Suspense fallback={<Loading />}><MuxProvidersPage /></Suspense>} />
      <Route path="/machines" component={() => <Suspense fallback={<Loading />}><MuxMachinesPage /></Suspense>} />
      {/* Bare /dashboard is never a stable coding URL — pin a machine. */}
      <Route path="/dashboard" component={DashboardHomeRedirect} />
      <Route path="/dashboard/machines" component={MachinesRoute} />
      <Route path="/dashboard/providers" component={ProvidersPageRoute} />
      <Route path="/dashboard/usage" component={UsagePageRoute} />
      <Route path="/dashboard/automations" component={AutomationsPageRoute} />
      <Route path="/dashboard/tools" component={ToolsPageRoute} />
      <Route path="/dashboard/settings" component={SettingsPageRoute} />
      <Route path="/dashboard/secrets" component={SecretsPageRoute} />
      <Route path="/dashboard/home" component={DashboardHomeRoute} />
      <Route path="/dashboard/m/:machine" component={HomeRoute} />
      <Route path="/dashboard/m/:machine/:dir" component={DirectoryLayout}>
        <Route path="/" component={SessionIndexRoute} />
        <Route path="/trash/:id" component={TrashPreviewRoute} />
        <Route path="/session/:id?" component={SessionPlaceholder} />
        <Route path="/oracle" component={OracleRoute} />
        <Route path="/oracle/summary" component={OracleSummaryRoute} />
      </Route>
      <Route path="/*all" component={() => <Navigate href="/" />} />
      </Router>
    </MachineResumeController>
  );
}

export function AppInterface(props: AppInterfaceProps) {
  return (
    <AuthProvider>
      <HostedApp {...props} />
    </AuthProvider>
  );
}
